import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import { toastSuccess, toastError } from '../common/toast';
import apiClient from '../../api/apiClient';
import PaymentTypesList from '../payments/PaymentTypesList';
import PaymentMethodsList from '../payments/PaymentMethodsList';
import InvoiceItemTypesList from '../invoicing/InvoiceItemTypesList';
import '../common/rtCommon.css';
import './PaymentSettings.css';
import RTPageLayout from '../common/RTPageLayout';

const PaymentSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('types');
  const [settings, setSettings] = useState({
    invoiceGenStartDate: 20,
    invoiceGenEndDate: 5,
    enableTransactionCharges: false,
    transactionChargePercentage: 0.5,
    // Payment provider settings
    paymentProvider: 'none',
    paymentProviderOptions: ['none', 'razorpay', 'phonepe'],
    ebFlatRateAc: '',
    ebFlatRateNonAc: '',
  });
  // Track the saved provider from database separately
  const [savedPaymentProvider, setSavedPaymentProvider] = useState('none');
  const [loading, setLoading] = useState(false);

  const transactionChargeOptions = [0.5, 1, 1.5, 2, 2.5, 3, 5];

  const paymentProviderLabels = {
    none: 'Disabled (No Payment Provider)',
    razorpay: 'Razorpay',
    phonepe: 'PhonePe',
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/payments/settings');
      const data = response.data;

      // Parse payment provider setting (JSON format)
      let paymentProvider = 'none';
      let paymentProviderOptions = ['none', 'razorpay', 'phonepe'];

      if (data.payment_provider?.value) {
        try {
          const providerData = JSON.parse(data.payment_provider.value);
          paymentProvider = providerData.selected || 'none';
          paymentProviderOptions = providerData.options || [
            'none',
            'razorpay',
            'phonepe',
          ];
          // Ensure 'none' is always available as an option
          if (!paymentProviderOptions.includes('none')) {
            paymentProviderOptions = ['none', ...paymentProviderOptions];
          }
        } catch (e) {
          // If not JSON, treat as simple string value
          paymentProvider = data.payment_provider.value;
        }
      }

      const newSettings = {
        invoiceGenStartDate: parseInt(data.invoice_gen_start_date?.value || 20),
        invoiceGenEndDate: parseInt(data.invoice_gen_end_date?.value || 5),
        enableTransactionCharges:
          data.enable_transaction_charges?.value === 'true' || false,
        transactionChargePercentage: parseFloat(
          data.transaction_charge_percentage?.value || 0.5,
        ),
        paymentProvider: paymentProvider,
        paymentProviderOptions: paymentProviderOptions,
        ebFlatRateAc: data.eb_flat_rate_ac?.value || '',
        ebFlatRateNonAc: data.eb_flat_rate_non_ac?.value || '',
      };

      setSettings(newSettings);
      setSavedPaymentProvider(paymentProvider); // Store the saved value from DB
      console.log('Payment settings loaded:', newSettings);
    } catch (error) {
      console.error('Error loading payment settings:', error);
      if (error.response?.status !== 404) {
        toastError('Failed to load payment settings');
      }
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      // Validation
      if (
        settings.invoiceGenStartDate < 1 ||
        settings.invoiceGenStartDate > 31
      ) {
        toastError('Invoice generation start date must be between 1 and 31');
        setLoading(false);
        return;
      }
      if (settings.invoiceGenEndDate < 1 || settings.invoiceGenEndDate > 31) {
        toastError('Invoice generation end date must be between 1 and 31');
        setLoading(false);
        return;
      }

      // Build payment provider value as JSON
      const paymentProviderValue = JSON.stringify({
        options: settings.paymentProviderOptions,
        selected: settings.paymentProvider,
      });

      const payloadData = {
        invoice_gen_start_date: settings.invoiceGenStartDate.toString(),
        invoice_gen_end_date: settings.invoiceGenEndDate.toString(),
        enable_transaction_charges:
          settings.enableTransactionCharges.toString(),
        transaction_charge_percentage:
          settings.transactionChargePercentage.toString(),
        payment_provider: paymentProviderValue,
      };

      await apiClient.post('/payments/settings', payloadData);

      // Update saved provider after successful save
      setSavedPaymentProvider(settings.paymentProvider);

      toastSuccess('Payment settings saved successfully');
      console.log('Saved settings:', settings);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toastError('Failed to save payment settings');
    } finally {
      setLoading(false);
    }
  };

  const saveEbSettings = async () => {
    const acVal =
      settings.ebFlatRateAc === '' ? '' : parseFloat(settings.ebFlatRateAc);
    const nonAcVal =
      settings.ebFlatRateNonAc === ''
        ? ''
        : parseFloat(settings.ebFlatRateNonAc);

    if (settings.ebFlatRateAc !== '' && (isNaN(acVal) || acVal <= 0)) {
      toastError('AC flat rate must be a positive number or empty');
      return;
    }
    if (settings.ebFlatRateNonAc !== '' && (isNaN(nonAcVal) || nonAcVal <= 0)) {
      toastError('Non AC flat rate must be a positive number or empty');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/payments/settings', {
        eb_flat_rate_ac: settings.ebFlatRateAc === '' ? '' : String(acVal),
        eb_flat_rate_non_ac:
          settings.ebFlatRateNonAc === '' ? '' : String(nonAcVal),
      });
      toastSuccess('EB bill rates saved successfully');
    } catch (error) {
      console.error('Error saving EB settings:', error);
      toastError('Failed to save EB bill rates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderPaymentTypes = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">Payment Types</h3>
      <div className="embedded-component">
        <PaymentTypesList embedded={true} />
      </div>
    </div>
  );

  const renderPaymentMethods = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">Payment Methods</h3>
      <div className="embedded-component">
        <PaymentMethodsList embedded={true} />
      </div>
    </div>
  );

  const renderInvoiceItemTypes = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">Invoice Item Types</h3>
      <div className="embedded-component">
        <InvoiceItemTypesList />
      </div>
    </div>
  );

  const renderInvoiceSettings = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">Invoice Generation Settings</h3>

      <div className="settings-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="invoiceGenStartDate">
              Invoice Generation Start Date
              <span className="label-hint">Day of the month (e.g., 20)</span>
            </label>
            <input
              type="number"
              id="invoiceGenStartDate"
              min="1"
              max="31"
              value={settings.invoiceGenStartDate}
              onChange={(e) =>
                handleInputChange(
                  'invoiceGenStartDate',
                  parseInt(e.target.value),
                )
              }
              className="rt-input"
            />
            <small className="input-help">
              Start generating invoices from this day of the month (typically
              20th)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="invoiceGenEndDate">
              Invoice Generation End Date
              <span className="label-hint">Day of the month (e.g., 5)</span>
            </label>
            <input
              type="number"
              id="invoiceGenEndDate"
              min="1"
              max="31"
              value={settings.invoiceGenEndDate}
              onChange={(e) =>
                handleInputChange('invoiceGenEndDate', parseInt(e.target.value))
              }
              className="rt-input"
            />
            <small className="input-help">
              Stop generating invoices after this day of the month (typically
              5th)
            </small>
          </div>
        </div>

        <div className="date-range-preview">
          <strong>Current Configuration:</strong>
          <p>
            Invoices can be generated from the{' '}
            <strong>{settings.invoiceGenStartDate}th</strong> of the current
            month to the <strong>{settings.invoiceGenEndDate}th</strong> of the
            next month for the upcoming billing period.
          </p>
          <div className="example-box">
            <strong>Example:</strong> From Nov {settings.invoiceGenStartDate} to
            Dec {settings.invoiceGenEndDate}, you can generate invoices for
            December
          </div>
        </div>
      </div>

      <div className="settings-save-section">
        <RTButton variant="primary" onClick={saveSettings} disabled={loading}>
          {loading ? 'Saving...' : 'Save Invoice Settings'}
        </RTButton>
      </div>
    </div>
  );

  const renderPaymentProviderSettings = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">Payment Provider Settings</h3>

      <div className="settings-form">
        <div className="provider-settings-row">
          {/* Left Column - Payment Provider Selection */}
          <div className="provider-column">
            <h4 className="subsection-title">Payment Gateway</h4>

            <div className="current-provider-text">
              {savedPaymentProvider === 'none'
                ? '⚠️ Payment Provider: Not Configured'
                : `✅ Current Active Provider: ${paymentProviderLabels[savedPaymentProvider] || savedPaymentProvider}`}
            </div>

            <div className="form-group">
              <label className="form-label">Select Payment Provider</label>
              <small
                className="input-help"
                style={{ marginBottom: '12px', display: 'block' }}
              >
                Choose the payment gateway for generating payment links
              </small>

              <div className="radio-group">
                {settings.paymentProviderOptions.map((provider) => (
                  <label key={provider} className="radio-label">
                    <input
                      type="radio"
                      name="paymentProvider"
                      value={provider}
                      checked={settings.paymentProvider === provider}
                      onChange={(e) =>
                        handleInputChange('paymentProvider', e.target.value)
                      }
                      className="radio-input"
                    />
                    <span className="radio-custom"></span>
                    <span className="radio-text">
                      <strong>
                        {paymentProviderLabels[provider] || provider}
                      </strong>
                      {provider === 'none' && (
                        <span className="provider-description">
                          - Payment links will not be generated
                        </span>
                      )}
                      {provider === 'razorpay' && (
                        <span className="provider-description">
                          - Supports UPI, Cards, Net Banking, Wallets
                        </span>
                      )}
                      {provider === 'phonepe' && (
                        <span className="provider-description">
                          - UPI-based payments via PhonePe
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Transaction Charges */}
          <div className="provider-column">
            <h4 className="subsection-title">Transaction Charges</h4>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={settings.enableTransactionCharges}
                  onChange={(e) =>
                    handleInputChange(
                      'enableTransactionCharges',
                      e.target.checked,
                    )
                  }
                  className="toggle-checkbox"
                />
                <span className="toggle-text">Enable Transaction Charges</span>
              </label>
              <small className="input-help">
                When enabled, transaction charges will be applied to payments
              </small>
            </div>

            {settings.enableTransactionCharges && (
              <div className="form-group">
                <label htmlFor="transactionChargePercentage">
                  Transaction Charge Percentage
                </label>
                <select
                  id="transactionChargePercentage"
                  value={settings.transactionChargePercentage}
                  onChange={(e) =>
                    handleInputChange(
                      'transactionChargePercentage',
                      parseFloat(e.target.value),
                    )
                  }
                  className="rt-select"
                >
                  {transactionChargeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}%
                    </option>
                  ))}
                </select>
                <small className="input-help">
                  Select the percentage to charge for transactions
                </small>
              </div>
            )}

            {settings.enableTransactionCharges && (
              <div className="charge-preview">
                <strong>Preview:</strong>
                <div className="preview-calculations">
                  <div className="preview-row">
                    <span>Amount:</span>
                    <span>₹1,000.00</span>
                  </div>
                  <div className="preview-row">
                    <span>
                      Transaction Charge ({settings.transactionChargePercentage}
                      %):
                    </span>
                    <span>
                      ₹
                      {(
                        (1000 * settings.transactionChargePercentage) /
                        100
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="preview-row total">
                    <span>Total Amount:</span>
                    <span>
                      ₹
                      {(
                        1000 +
                        (1000 * settings.transactionChargePercentage) / 100
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button - Centered at Bottom */}
      <div className="settings-save-section centered">
        <RTButton variant="primary" onClick={saveSettings} disabled={loading}>
          {loading ? 'Saving...' : 'Save Payment Provider Settings'}
        </RTButton>
      </div>
    </div>
  );

  const renderEbBillSettings = () => (
    <div className="payment-settings-section">
      <h3 className="section-title">EB Bill Flat Rates</h3>
      <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>
        Configure flat EB charge amounts applied by room type in Add EB Bill to
        Monthly Rental Invoices. Leave blank if not used for that room type.
      </p>
      <div className="settings-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ebFlatRateAc">Flat EB Rate — AC Rooms (₹)</label>
            <input
              type="number"
              id="ebFlatRateAc"
              min="0"
              step="0.01"
              value={settings.ebFlatRateAc}
              onChange={(e) =>
                handleInputChange('ebFlatRateAc', e.target.value)
              }
              className="rt-input"
              placeholder="e.g. 500"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ebFlatRateNonAc">
              Flat EB Rate — Non AC Rooms (₹)
            </label>
            <input
              type="number"
              id="ebFlatRateNonAc"
              min="0"
              step="0.01"
              value={settings.ebFlatRateNonAc}
              onChange={(e) =>
                handleInputChange('ebFlatRateNonAc', e.target.value)
              }
              className="rt-input"
              placeholder="e.g. 250"
            />
          </div>
        </div>
      </div>
      <div className="settings-save-section">
        <RTButton variant="primary" onClick={saveEbSettings} disabled={loading}>
          {loading ? 'Saving...' : 'Save EB Bill Rates'}
        </RTButton>
      </div>
    </div>
  );

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Payment Settings"
      actions={
        <>
          <RTButton type="secondary" onClick={() => navigate('/settings')}>
            &#8592; Back to Settings
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <div className="payment-settings-container">
        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => setActiveTab('types')}
          >
            <span className="tab-icon">🏷️</span>
            Payment Types
          </button>
          <button
            className={`tab-button ${activeTab === 'methods' ? 'active' : ''}`}
            onClick={() => setActiveTab('methods')}
          >
            <span className="tab-icon">💳</span>
            Payment Methods
          </button>
          <button
            className={`tab-button ${activeTab === 'itemTypes' ? 'active' : ''}`}
            onClick={() => setActiveTab('itemTypes')}
          >
            <span className="tab-icon">📋</span>
            Invoice Item Types
          </button>
          <button
            className={`tab-button ${activeTab === 'invoice' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoice')}
          >
            <span className="tab-icon">📅</span>
            Invoice Generation
          </button>
          <button
            className={`tab-button ${activeTab === 'provider' ? 'active' : ''}`}
            onClick={() => setActiveTab('provider')}
          >
            <span className="tab-icon">🏦</span>
            Payment Provider
          </button>
          <button
            className={`tab-button ${activeTab === 'ebBill' ? 'active' : ''}`}
            onClick={() => setActiveTab('ebBill')}
          >
            <span className="tab-icon">⚡</span>
            EB Bill Rates
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'types' && renderPaymentTypes()}
          {activeTab === 'methods' && renderPaymentMethods()}
          {activeTab === 'itemTypes' && renderInvoiceItemTypes()}
          {activeTab === 'invoice' && renderInvoiceSettings()}
          {activeTab === 'provider' && renderPaymentProviderSettings()}
          {activeTab === 'ebBill' && renderEbBillSettings()}
        </div>
      </div>
    </RTPageLayout>
  );
};

export default PaymentSettings;
